import {
	getRefreshTokenFromLocalStorage,
	getAccessTokenFromLocalStorage,
	setRefreshTokenInLocalStorage,
	setAccessTokenInLocalStorage,
	removeTokensFromLocalStorage,
	setIsRefreshingTokenInLocalStorage,
	getIsRefreshingTokenFromLocalStorage,
	getAccountOrganizationIdsFromLocalStorage,
	setAccountOrganizationIdsInLocalStorage,
} from './storage/auth-localstorage'
import {
	getAccessTokenFromSessionStorage,
	getAccountOrganizationIdsFromSessionStorage,
	getIsRefreshingTokenFromSessionStorage,
	getRefreshTokenFromSessionStorage,
	removeTokensFromSessionStorage,
	setAccessTokenInSessionStorage,
	setAccountOrganizationIdsInSessionStorage,
	setIsRefreshingTokenInSessionStorage,
	setRefreshTokenInSessionStorage,
} from './storage/auth-sessionStorage'

export const getRefreshToken = (): string | null => {
	return getRefreshTokenFromLocalStorage() ?? getRefreshTokenFromSessionStorage()
}

export const getAccessToken = (): string | null => {
	return getAccessTokenFromLocalStorage() ?? getAccessTokenFromSessionStorage()
}

type AccountOrganizationIds = Record<string, string>

// Kept next to the refresh token, so they last as long as the session does.
const getAccountOrganizationIds = (): AccountOrganizationIds => {
	const value = getRefreshTokenFromLocalStorage()
		? getAccountOrganizationIdsFromLocalStorage()
		: getAccountOrganizationIdsFromSessionStorage()
	try {
		const parsed: unknown = JSON.parse(value ?? '{}')
		return typeof parsed === 'object' && parsed !== null ? (parsed as AccountOrganizationIds) : {}
	} catch {
		return {}
	}
}

const setAccountOrganizationIds = (accountOrganizationIds: AccountOrganizationIds): void => {
	const value = JSON.stringify(accountOrganizationIds)
	getRefreshTokenFromLocalStorage()
		? setAccountOrganizationIdsInLocalStorage(value)
		: setAccountOrganizationIdsInSessionStorage(value)
}

/** The organization the account was last opened in */
export const getOrganizationId = (accountId: string): string | null => {
	return getAccountOrganizationIds()[accountId] ?? null
}

export const getIsRefreshingToken = (): boolean => {
	return getIsRefreshingTokenFromLocalStorage() ?? getIsRefreshingTokenFromSessionStorage() ?? false
}

/**
 * Sets the refresh token in the correct location depending on what should be used
 * @param refreshToken
 */
export const setRefreshToken = (refreshToken: string): void => {
	getRefreshTokenFromLocalStorage()
		? setRefreshTokenInLocalStorage(refreshToken)
		: setRefreshTokenInSessionStorage(refreshToken)
}

export const setOrganizationId = (accountId: string, organizationId: string): void => {
	setAccountOrganizationIds({ ...getAccountOrganizationIds(), [accountId]: organizationId })
}

/**
 * Sets the access token in the correct location depending on what should be used
 * @param accessToken
 */
export const setAccessToken = (accessToken: string): void => {
	getAccessTokenFromLocalStorage()
		? setAccessTokenInLocalStorage(accessToken)
		: setAccessTokenInSessionStorage(accessToken)
}

/**
 * Sets if token is refreshed in the correct location depending on what should be used
 * @param accessToken
 */
export const setIsRefreshingToken = (isRefreshing: boolean): void => {
	getAccessTokenFromLocalStorage()
		? setIsRefreshingTokenInLocalStorage(isRefreshing)
		: setIsRefreshingTokenInSessionStorage(isRefreshing)
}

export const removeTokens = (): void => {
	removeTokensFromLocalStorage()
	removeTokensFromSessionStorage()
}
